/**
 * Robot commission / decommission (plan §7.2).
 *
 * Decommissioning is upstream's `Commission::decommission()` — the
 * robot refuses new dispatched tasks, new direct tasks, and its own
 * idle behaviours, and its QUEUED work is redistributed within its own
 * fleet. It is NOT a stop button: the task the robot is currently
 * executing keeps running (upstream `TaskManager::
 * _drain_dispatched_assignments` drains queued assignments only), so
 * the UI that offers this must say so.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import {
  commissionRobot,
  confirmDustbag,
  fireRelocalize,
  fetchRobotIdlePolicies,
  setRobotIdleBehavior,
} from '../v1-client';
import { fetchFleetsSnapshot } from '../v1-zenoh-client';
import { v1Key } from '../v1';
import { fleetsQueryOptions, robotsQueryOptions } from '../query-options';
import type {
  FleetRegistry,
  RobotCommissionRequest,
  RobotCommissionResponse,
  RobotCommissionState,
  RobotConfirmDustbagResponse,
  RobotFireRelocalizeResponse,
  RobotIdleBehaviorResponse,
  RobotIdlePolicy,
  SiteId,
} from '../v1';

export interface CommissionRobotVariables {
  fleet: string;
  robot: string;
  action: RobotCommissionRequest['action'];
  /** Which flags to restore; only meaningful for `commission`. */
  commission?: RobotCommissionRequest['commission'];
  pendingDispatchTasksPolicy?: RobotCommissionRequest['pending_dispatch_tasks_policy'];
  pendingDirectTasksPolicy?: RobotCommissionRequest['pending_direct_tasks_policy'];
}

/**
 * Map key for a commission state.
 *
 * Fleet-qualified, NOT the bare robot name: RMF only guarantees a
 * robot name is unique WITHIN its fleet (`fleet_state.robots` is a
 * per-fleet map), so two fleets at one site may both publish a
 * `robot_1`. Keyed by name alone, the later fleet in the registry
 * array silently overwrote the earlier one and both rows rendered the
 * loser's badge — which also hid `Recommission` on a genuinely
 * decommissioned robot, leaving it unrecoverable from this UI.
 * `fire_mode.py` already keys its own decommission ledger `f"{fleet}/
 * {robot}"` for the same reason.
 */
export const commissionKey = (fleet: string, robot: string): string => `${fleet}/${robot}`;

/**
 * Commission state per fleet-qualified robot, for the active site.
 *
 * `commission_state` lives on `RobotRegistry` (the `fleets` category),
 * not on `RobotState` (the `robots` category) — the registry is static
 * data the state stream must never overwrite. `useFleets()` can't
 * serve it because `adaptFleetRegistry` reduces each fleet to a name
 * and a robot COUNT, dropping the per-robot registry rows entirely, so
 * this reads the raw snapshot instead.
 *
 * The key extends the `fleets` key rather than standing beside it:
 * TanStack matches `invalidateQueries` by prefix, so the live
 * transport's own fleets re-snapshot (`useLiveData`'s
 * `requestResnapshot`) refreshes this too. Live fleet EVENTS are
 * applied with `setQueryData` against the exact fleets key and do not
 * reach here, hence the poll.
 */
/**
 * What one robot's registry row says about its service state.
 *
 * `state` is the settled tri-state and `idleBehavior` the raw
 * `commission.idle_behavior` flag underneath it, because those two
 * answer different questions and the tri-state cannot answer the
 * second: a robot bidding for and running work with automatic
 * charging switched off reads `unknown`, which is indistinguishable
 * from a fleet adapter that published nothing at all. `null` means
 * exactly that — nothing published — and is NOT "on".
 *
 * The other two raw flags travel with it because "off" alone does not
 * say WHY the tri-state is `unknown`: automatic charging is the only
 * partial commission this console writes, but the task API accepts a
 * `robot_commission_request` from anything, so a robot can arrive
 * refusing direct work as well. The drawer needs both to know whether
 * the auto-charge chip is the whole explanation.
 */
export interface RobotCommissionInfo {
  state: RobotCommissionState;
  idleBehavior: boolean | null;
  dispatchTasks: boolean | null;
  directTasks: boolean | null;
  /**
   * The issue categories the fleet adapter is currently reporting for
   * this robot, verbatim. Read from the same registry row rather than
   * from a second query: they arrive together and a separate fetch
   * would let the two disagree about one robot.
   */
  issues: string[];
}

/**
 * The issue the Gausium adapter raises when it holds a robot after a
 * fire alarm clears (`_ISSUE_FIRE_RELOCALIZE`, adapter 0.7.6). Matched
 * verbatim because the adapter publishes the operator-facing sentence
 * itself -- there is no code beside it to key on.
 */
export const FIRE_RELOCALIZE_ISSUE = 'confirm which floor this robot is on';
/** Fleet adapter 0.7.19: its e-stop held a lift, the car was handed back
 *  with the robot in it or its doorway, and its job is paused until it is
 *  back on the map. "Confirm floor and release" (ops) ends that pause too;
 *  hotel staff see the same press as "Scanned it". */
export const ESTOP_RELOCALIZE_ISSUE = 'relocalize after lift';

export function useRobotCommissionStates() {
  const siteId = useSafeSiteId();
  return useQuery<Map<string, RobotCommissionInfo>>({
    queryKey: siteId
      ? v1Key(
          { site: siteId as SiteId, category: 'fleets', epoch: 'current', schema_version: '1.0' },
          'registry',
        )
      : (['v1', '__none__', 'fleets', 'current', '1.0', 'registry'] as const),
    queryFn: async () => {
      const registries: FleetRegistry[] = await fetchFleetsSnapshot(siteId as SiteId);
      const byRobot = new Map<string, RobotCommissionInfo>();
      for (const fleet of registries) {
        for (const robot of fleet.robots) {
          byRobot.set(commissionKey(robot.id.fleet, robot.id.robot), {
            state: robot.commission_state,
            // `?? null` and not `?? true`: a worker older than 0.4.11
            // publishes no `commission` object, and reading that
            // silence as "automatic charging is on" would draw a
            // confident ON chip for a flag nobody has reported.
            idleBehavior: robot.commission?.idle_behavior ?? null,
            dispatchTasks: robot.commission?.dispatch_tasks ?? null,
            directTasks: robot.commission?.direct_tasks ?? null,
            issues: Array.isArray(robot.issues) ? robot.issues : [],
          });
        }
      }
      return byRobot;
    },
    enabled: Boolean(siteId),
    staleTime: 10_000,
    refetchInterval: 15_000,
  });
}

export function useCommissionRobot() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<RobotCommissionResponse, Error, CommissionRobotVariables>({
    mutationFn: (vars) =>
      commissionRobot(siteId as SiteId, {
        robot: { site: siteId as SiteId, fleet: vars.fleet, robot: vars.robot },
        action: vars.action,
        ...(vars.commission ? { commission: vars.commission } : {}),
        // Omitted, not nulled, when the operator didn't choose: an
        // absent `pending_dispatch_tasks_policy` is what leaves
        // upstream to apply its own "reassign" default, and the
        // api-server drops the key on the same reasoning.
        ...(vars.pendingDispatchTasksPolicy
          ? { pending_dispatch_tasks_policy: vars.pendingDispatchTasksPolicy }
          : {}),
        ...(vars.pendingDirectTasksPolicy
          ? { pending_direct_tasks_policy: vars.pendingDirectTasksPolicy }
          : {}),
        idempotency_key: crypto.randomUUID(),
      }),
    // The badge this drives reads from the fleets registry, which the
    // worker only republishes once RMF has actually applied the
    // change — invalidating without refetching would leave the drawer
    // showing the pre-command state until the next poll.
    onSuccess: () => {
      if (!siteId) return;
      const site = siteId as SiteId;
      void qc.invalidateQueries({ queryKey: fleetsQueryOptions(site).queryKey });
      void qc.invalidateQueries({ queryKey: robotsQueryOptions(site).queryKey });
    },
  });
}

// -----------------------------------------------------------------------------
// Automatic charging (RMF idle behaviour)
// -----------------------------------------------------------------------------

/**
 * The site's stored "automatic charging is off" rows.
 *
 * This is DESIRED state and has no live counterpart: the fleet state
 * carries the flag RMF is actually holding, but nothing on the wire
 * carries the expiry the operator chose, so the countdown can only
 * come from here. Read in every role — a service operator who cannot
 * change the setting still needs to know the robot will not take
 * itself back to its charger.
 */
export const idlePoliciesKey = (siteId: string) =>
  ['v1', siteId, 'idle_policies', 'current', '1.0'] as const;

export function useRobotIdlePolicies() {
  const siteId = useSafeSiteId();
  return useQuery<RobotIdlePolicy[]>({
    queryKey: idlePoliciesKey(siteId || '__none__'),
    queryFn: () => fetchRobotIdlePolicies(siteId as SiteId),
    enabled: Boolean(siteId),
    staleTime: 10_000,
    refetchInterval: 15_000,
  });
}

export interface SetRobotIdleBehaviorVariables {
  fleet: string;
  robot: string;
  enabled: boolean;
  /** Minutes until it switches back on by itself; `null` is "until
   *  turned back on" and the api-server allows it for platform admins
   *  only. Ignored when `enabled` is true. */
  ttlMinutes?: number | null;
  reason?: string;
}

export function useSetRobotIdleBehavior() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<RobotIdleBehaviorResponse, Error, SetRobotIdleBehaviorVariables>({
    mutationFn: (vars) =>
      setRobotIdleBehavior(siteId as SiteId, {
        robot: { site: siteId as SiteId, fleet: vars.fleet, robot: vars.robot },
        enabled: vars.enabled,
        // Sent only for an "off": `ttl_minutes` is meaningless
        // alongside `enabled: true` and the api-server ignores it, so
        // omitting it keeps the audited target honest about what was
        // asked for.
        ...(vars.enabled ? {} : { ttl_minutes: vars.ttlMinutes ?? null }),
        ...(vars.reason?.trim() ? { reason: vars.reason.trim() } : {}),
        idempotency_key: crypto.randomUUID(),
      }),
    onSuccess: () => {
      if (!siteId) return;
      const site = siteId as SiteId;
      const refresh = () => {
        // Prefix match: the registry sub-key `useRobotCommissionStates`
        // reads lives under the fleets key, so this refetches the live
        // flag as well as the fleet list.
        void qc.invalidateQueries({ queryKey: fleetsQueryOptions(site).queryKey });
        void qc.invalidateQueries({ queryKey: idlePoliciesKey(site) });
      };
      refresh();
      // And again, once. The policy row is written before the response
      // returns, but the LIVE flag only changes when RMF has applied
      // the command and the fleet adapter has published its next state
      // -- 1-2 s later. Without this second pass the chip sits on
      // "applying..." until the 15 s poll comes round, which reads as a
      // command that did not take.
      setTimeout(refresh, 3_000);
    },
  });
}

// -----------------------------------------------------------------------------
// Dustbag acknowledgement (W11)
// -----------------------------------------------------------------------------

export interface ConfirmDustbagVariables {
  fleet: string;
  robot: string;
  /** The waiting task, when the caller knows it. The task drawer
   *  always does; the robot drawer only does while `robot.taskId` is
   *  populated. OMITTED rather than nulled when absent -- see
   *  `confirmDustbag`. */
  taskId?: string;
}

/**
 * "The bag is empty, carry on" (`robot.confirm_dustbag`).
 *
 * INVALIDATES NOTHING. The confirm travels api-server -> worker ->
 * `robot_action_confirmations` -> the fleet adapter, which is what
 * ends the adapter's ack wait and lets the task finish; the task then
 * moves to `completed` in the ordinary projection, over the live
 * overlay, a second or two later. A refetch here would race that with
 * a snapshot taken before the adapter had even received the confirm,
 * and a task still showing "active" immediately after a successful
 * press reads as a press that did not land.
 *
 * The key is minted per press. Unlike a door override there is no
 * "extend" case -- a second confirm for the same bag is the same
 * assertion -- but the adapter latches a consumed confirm per task
 * anyway (it has to: an emergency interrupt re-invokes `execute_action`
 * from scratch with no callback), so a duplicate is absorbed there
 * rather than needing to be prevented here.
 */
export function useConfirmDustbag() {
  const siteId = useSafeSiteId();
  return useMutation<RobotConfirmDustbagResponse, Error, ConfirmDustbagVariables>({
    mutationFn: (vars) =>
      confirmDustbag(siteId as SiteId, {
        robot: { site: siteId as SiteId, fleet: vars.fleet, robot: vars.robot },
        ...(vars.taskId ? { task_id: vars.taskId } : {}),
        idempotency_key: crypto.randomUUID(),
      }),
  });
}

/**
 * "I have confirmed which floor this robot is on" (`robot.fire_relocalize`).
 *
 * INVALIDATES NOTHING, for `useConfirmDustbag`'s reason: the release
 * travels api-server -> worker -> `robot_action_confirmations` -> the
 * fleet adapter, which resumes the interrupted task; the robot's issue
 * then clears in the ordinary registry poll a few seconds later. A
 * refetch here would race a snapshot taken before the adapter had even
 * received the document.
 *
 * A second press when nothing is held is a no-op at the adapter (its
 * release returns False and nothing happens), so the key is minted per
 * press and no de-duplication is attempted here.
 */
export function useFireRelocalize() {
  const siteId = useSafeSiteId();
  return useMutation<RobotFireRelocalizeResponse, Error, { fleet: string; robot: string }>({
    mutationFn: (vars) =>
      fireRelocalize(siteId as SiteId, {
        robot: { site: siteId as SiteId, fleet: vars.fleet, robot: vars.robot },
        idempotency_key: crypto.randomUUID(),
      }),
  });
}
